import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Breadcrumbs,
  Link,
} from '@mui/joy';
import {
  Plus as AddIcon,
  PencilSimple as EditIcon,
  Eye as ViewIcon,
  Trash as DeleteIcon,
  Gear as SettingsIcon,
  CaretDown as ExpandMore,
  CaretRight as ChevronRight,
  FolderOpen,
  FileText as Description,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { getSignedPreviewUrl } from '../../lib/previewUrl';

interface Page {
  id: number;
  slug: string;
  template_key: string;
  status: 'draft' | 'published';
  seo_draft: {
    title?: string;
    description?: string;
  };
  version_counter: number;
  updated_at: string;
  parent: number | null;
  children?: Page[];
  depth?: number;
  order?: number;
}

interface PageTreeItemProps {
  page: Page;
  depth: number;
  expandedNodes: Set<number>;
  toggleExpanded: (pageId: number) => void;
  siteId: string;
  site: any;
  navigate: any;
}

const PageTreeItem: React.FC<PageTreeItemProps> = ({
  page,
  depth,
  expandedNodes,
  toggleExpanded,
  siteId,
  site,
  navigate,
}) => {
  const hasChildren = page.children && page.children.length > 0;
  const isExpanded = expandedNodes.has(page.id);

  return (
    <>
      <Card variant="outlined" sx={{ ml: depth * 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {hasChildren && (
                  <IconButton
                    size="sm"
                    onClick={() => toggleExpanded(page.id)}
                    sx={{ p: 0.5 }}
                  >
                    {isExpanded ? <ExpandMore /> : <ChevronRight />}
                  </IconButton>
                )}
                {hasChildren ? (
                  <FolderOpen size={20} color="var(--joy-palette-primary-500)" />
                ) : (
                  <Description size={20} color="var(--joy-palette-neutral-500)" />
                )}
                <Typography level="title-lg">
                  {page.seo_draft?.title || page.slug}
                </Typography>
                <Chip
                  size="sm"
                  color={page.status === 'published' ? 'success' : 'warning'}
                  variant={page.status === 'published' ? 'solid' : 'soft'}
                >
                  {page.status === 'published' ? `Published v${page.version_counter}` : 'Draft'}
                </Chip>
              </Box>

              <Typography level="body-sm" sx={{ mb: 1, ml: hasChildren ? 5.5 : 3.5 }}>
                <strong>URL:</strong> /{page.slug === 'index' ? '' : page.slug}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="sm"
                onClick={async () => {
                  const url = await getSignedPreviewUrl(siteId, page.slug);
                  window.open(url, '_blank', 'noopener');
                }}
              >
                <ViewIcon />
              </IconButton>
              <IconButton
                size="sm"
                color="primary"
                onClick={() => navigate(`/cms/sites/${siteId}/pages/${page.id}`)}
              >
                <EditIcon />
              </IconButton>
              <IconButton size="sm" color="danger">
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      {hasChildren && isExpanded && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {page.children!.map((childPage) => (
            <PageTreeItem
              key={childPage.id}
              page={childPage}
              depth={depth + 1}
              expandedNodes={expandedNodes}
              toggleExpanded={toggleExpanded}
              siteId={siteId}
              site={site}
              navigate={navigate}
            />
          ))}
        </Box>
      )}
    </>
  );
};

export const PageList: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
  const [initialized, setInitialized] = useState(false);

  const { data: pages, isLoading } = useQuery({
    queryKey: ['cms-pages', siteId],
    queryFn: async () => {
      const response = await api.get<any>(`/api/cms/sites/${siteId}/pages/`);
      return (response.data?.results || response.data || []) as Page[];
    },
    enabled: !!siteId,
  });

  const { data: site } = useQuery({
    queryKey: ['cms-site', siteId],
    queryFn: async () => {
      const response = await api.get<any>(`/api/cms/sites/${siteId}/`);
      return response.data;
    },
    enabled: !!siteId,
  });

  // Build tree structure from flat page list
  const pageTree = useMemo(() => {
    if (!pages) return [];
    
    // Create a map for quick lookups
    const pageMap = new Map<number, Page>();
    const rootPages: Page[] = [];
    
    // First pass: create map and identify root pages
    pages.forEach(page => {
      pageMap.set(page.id, { ...page, children: [] });
    });
    
    // Second pass: build tree structure
    pages.forEach(page => {
      const currentPage = pageMap.get(page.id)!;
      if (page.parent && pageMap.has(page.parent)) {
        const parentPage = pageMap.get(page.parent)!;
        if (!parentPage.children) parentPage.children = [];
        parentPage.children.push(currentPage);
      } else {
        rootPages.push(currentPage);
      }
    });
    
    // Sort pages by order or slug
    const sortPages = (pageList: Page[]) => {
      pageList.sort((a, b) => {
        // Put index/homepage first
        if (a.slug === 'index') return -1;
        if (b.slug === 'index') return 1;
        // Then sort by order if available, otherwise by slug
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return a.slug.localeCompare(b.slug);
      });
      // Recursively sort children
      pageList.forEach(page => {
        if (page.children && page.children.length > 0) {
          sortPages(page.children);
        }
      });
    };
    
    sortPages(rootPages);
    return rootPages;
  }, [pages]);

  const toggleExpanded = (pageId: number) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pageId)) {
        newSet.delete(pageId);
      } else {
        newSet.add(pageId);
      }
      return newSet;
    });
  };

  // Initially expand all parent pages to show hierarchy
  React.useEffect(() => {
    if (pages && !initialized) {
      const parentsSet = new Set<number>();
      pages.forEach(page => {
        // If this page has children, add it to expanded set
        if (pages.some(p => p.parent === page.id)) {
          parentsSet.add(page.id);
        }
      });
      setExpandedNodes(parentsSet);
      setInitialized(true);
    }
  }, [pages, initialized]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs separator="›" sx={{ mb: 2 }}>
        <Link onClick={() => navigate('/cms/sites')}>Sites</Link>
        <Typography>{site?.name || 'Site'}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography level="h2">Pages</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined"
            color="primary"
            startDecorator={<SettingsIcon />}
            onClick={() => navigate(`/cms/sites/${siteId}/settings`)}
          >
            Site Settings
          </Button>
          <Button 
            startDecorator={<AddIcon />}
            onClick={() => navigate(`/cms/sites/${siteId}/pages/new`)}
          >
            Add Page
          </Button>
        </Box>
      </Box>

      {pages?.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography level="body-md" sx={{ mb: 2 }}>
                No pages created yet
              </Typography>
              <Button 
                startDecorator={<AddIcon />}
                onClick={() => navigate(`/cms/sites/${siteId}/pages/new`)}
              >
                Create First Page
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {pageTree.map((page) => (
            <PageTreeItem
              key={page.id}
              page={page}
              depth={0}
              expandedNodes={expandedNodes}
              toggleExpanded={toggleExpanded}
              siteId={siteId!}
              site={site}
              navigate={navigate}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};